import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Helper for auth headers
const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// Initial State
const initialState = {
  adlRecords: [],
  currentAdlRecord: null,
  currentPatient: null,
  isLoading: false,
  error: null,
  adlActivities: [],
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all ADL Records (optionally by patient ID)
export const fetchAdlRecords = createAsyncThunk(
  'adlRecords/fetchAdlRecords',
  async (patientId = null, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      const url = patientId
        ? `/adl-records?patient=${patientId}`
        : '/adl-records';

      const response = await api.get(url, {
        headers: getAuthHeaders(token),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch ADL Records'
      );
    }
  }
);

// Fetch ADL Activities (master table)
export const fetchAdlActivities = createAsyncThunk(
  'adlRecords/fetchAdlActivities',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      const response = await api.get('/adl-records/activities', {
        headers: getAuthHeaders(token),
      });

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch ADL Activities'
      );
    }
  }
);

// Fetch single ADL Record by ID
export const fetchAdlRecord = createAsyncThunk(
  'adlRecords/fetchAdlRecord',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      const response = await api.get(`/adl-records/${id}`, {
        headers: getAuthHeaders(token),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch ADL Record'
      );
    }
  }
);

// Create new ADL Record
export const createAdlRecord = createAsyncThunk(
  'adlRecords/createAdlRecord',
  async (adlRecordData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      const response = await api.post(
        '/adl-records',
        adlRecordData,
        {
          headers: getAuthHeaders(token),
        }
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create ADL Record'
      );
    }
  }
);

// Update ADL Record
export const updateAdlRecord = createAsyncThunk(
  'adlRecords/updateAdlRecord',
  async ({ case_id, adlRecordData }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      const response = await api.put(
        `/adl-records/case/${case_id}`,
        adlRecordData,
        {
          headers: getAuthHeaders(token),
        }
      );

      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to update ADL Record'
      );
    }
  }
);

// Delete ADL Record
export const deleteAdlRecord = createAsyncThunk(
  'adlRecords/deleteAdlRecord',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      await api.delete(`/adl-records/${id}`, {
        headers: getAuthHeaders(token),
      });
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete ADL Record'
      );
    }
  }
);

// Fetch ADL Records by patient ID
export const fetchAdlRecordsByPatient = createAsyncThunk(
  'adlRecords/fetchAdlRecordsByPatient',
  async (patientId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      if (!token) return rejectWithValue('No authentication token available');

      const response = await api.get(`/adl-records/patient/${patientId}`, {
        headers: getAuthHeaders(token),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch ADL Records by patient'
      );
    }
  }
);

/* ==============================
   SLICE
============================== */

const adlRecordsSlice = createSlice({
  name: 'adlRecords',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAdlRecord: (state, action) => {
      state.currentAdlRecord = action.payload;
    },
    clearCurrentAdlRecord: (state) => {
      state.currentAdlRecord = null;
    },
    setCurrentPatient: (state, action) => {
      state.currentPatient = action.payload;
    },
    clearCurrentPatient: (state) => {
      state.currentPatient = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ADL ACTIVITIES
      .addCase(fetchAdlActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdlActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adlActivities = action.payload;
      })
      .addCase(fetchAdlActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // FETCH ALL RECORDS
      .addCase(fetchAdlRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdlRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adlRecords = action.payload;
      })
      .addCase(fetchAdlRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // FETCH RECORDS BY PATIENT
      .addCase(fetchAdlRecordsByPatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdlRecordsByPatient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adlRecords = action.payload;
      })
      .addCase(fetchAdlRecordsByPatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // FETCH SINGLE RECORD
      .addCase(fetchAdlRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdlRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAdlRecord = action.payload;
      })
      .addCase(fetchAdlRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // CREATE RECORD
      .addCase(createAdlRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAdlRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adlRecords.unshift(action.payload);
      })
      .addCase(createAdlRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // UPDATE RECORD
      .addCase(updateAdlRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAdlRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const updatedRecordId = updated?.record?.id ?? updated?.id;
        const index = state.adlRecords.findIndex((record) => {
          const recordId = record?.record?.id ?? record?.id;
          return recordId === updatedRecordId;
        });
        if (index !== -1) {
          state.adlRecords[index] = updated;
        }
        if (state.currentAdlRecord) {
          const currentRecordId = state.currentAdlRecord?.record?.id ?? state.currentAdlRecord?.id;
          if (currentRecordId === updatedRecordId) {
            state.currentAdlRecord = updated;
          }
        }
      })
      .addCase(updateAdlRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to update ADL Record';
      })
      // DELETE RECORD
      .addCase(deleteAdlRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAdlRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adlRecords = state.adlRecords.filter(
          (record) => record.record?.id !== action.payload && record.id !== action.payload
        );
        if (state.currentAdlRecord?.record?.id === action.payload || state.currentAdlRecord?.id === action.payload) {
          state.currentAdlRecord = null;
        }
      })
      .addCase(deleteAdlRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearError, 
  setCurrentAdlRecord, 
  clearCurrentAdlRecord,
  setCurrentPatient,
  clearCurrentPatient 
} = adlRecordsSlice.actions;

export default adlRecordsSlice.reducer;